<template>
  <div class="md-file-editor">
    <div class="editor-header">
      <div class="header-left">
        <b-button variant="link" @click="goBack" class="back-button">
          <i class="fa fa-arrow-left"></i>
        </b-button>
        <h4 class="mb-0">{{ fileName }}</h4>
      </div>

      <div class="header-right">
        <b-button-group class="mr-2">
          <b-button
            :variant="viewMode === 'edit' ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="viewMode = 'edit'"
          >
            <i class="fa fa-pencil"></i> Edit
          </b-button>
          <b-button
            :variant="viewMode === 'split' ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="viewMode = 'split'"
          >
            <i class="fa fa-columns"></i> Split
          </b-button>
          <b-button
            :variant="viewMode === 'preview' ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="viewMode = 'preview'"
          >
            <i class="fa fa-eye"></i> Preview
          </b-button>
        </b-button-group>

        <b-button
          variant="success"
          size="sm"
          @click="saveMd"
          :disabled="saving"
        >
          <b-spinner small v-if="saving" />
          <template v-else>
            <i class="fa fa-save"></i> Save
          </template>
        </b-button>
      </div>
    </div>

    <div class="editor-body" :class="viewMode">
      <div class="editor-pane" v-if="viewMode !== 'preview'">
        <MonacoEditor
          v-model="content"
          language="markdown"
          :height="editorHeight"
          :options="monacoOptions"
        />
      </div>

      <div class="preview-pane" v-if="viewMode !== 'edit'">
        <div class="preview-content markdown-body" v-html="renderedHtml"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import MonacoEditor from '@/components/form/Editor/MonacoEditor.vue';

marked.setOptions({
  gfm: true,
  breaks: true
});

export default {
  name: 'MdFileEditor',
  components: {
    MonacoEditor
  },
  data() {
    return {
      fileName: '',
      fileKey: '',
      content: '',
      saving: false,
      viewMode: 'split',
      editorHeight: 600,
      monacoOptions: {
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        theme: 'vs-dark',
        lineNumbers: 'on',
        fontSize: 14
      }
    };
  },
  computed: {
    renderedHtml() {
      return marked(this.content || '');
    }
  },
  mounted() {
    this.fileName = this.$route.query.name || 'Untitled.md';
    const path = this.$route.query.path || '';
    this.fileKey = path ? `${path}/${this.fileName}` : this.fileName;

    this.loadContent();
    this.calculateEditorHeight();
    window.addEventListener('resize', this.calculateEditorHeight);

    if (!window.kortex) return
    var self = this

    window.kortex.registerContext(
      'Markdown Editor',
      'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
      'The user is editing a Markdown file named "' + this.fileName + '". ' +
      'You can read and write the full Markdown content, save the file, and insert formatted sections.',
      'fa-file-text-o'
    )
    window.kortex.showHelper()

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_md_file_info',
          description: 'Returns the current Markdown file name and key/path.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        return { file_name: self.fileName, file_key: self.fileKey }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_md_content',
          description: 'Returns the full Markdown source of the file currently open in the editor.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        return { content: self.content, length: (self.content || '').length }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_md_content',
          description: 'Replaces the entire Markdown content in the editor with the given text.',
          parameters: {
            type: 'object',
            required: ['content'],
            properties: {
              content: { type: 'string', description: 'The new Markdown content to place in the editor.' }
            }
          }
        }
      },
      function (args) {
        if (!args.content && args.content !== '') return { error: 'content is required.' }
        self.content = args.content
        return { ok: true, length: args.content.length }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'append_md_content',
          description: 'Appends text at the end of the current Markdown content.',
          parameters: {
            type: 'object',
            required: ['text'],
            properties: {
              text: { type: 'string', description: 'The Markdown text to append.' }
            }
          }
        }
      },
      function (args) {
        if (!args.text) return { error: 'text is required.' }
        self.content = (self.content || '') + '\n' + args.text
        return { ok: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'save_md_file',
          description: 'Saves the current Markdown content to storage.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        return self.saveMd().then(function () {
          return { saved: true, file: self.fileName }
        }).catch(function (e) {
          return { error: String(e.message || e) }
        })
      }
    )
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateEditorHeight);
    if (window.kortex) {
      window.kortex.unregisterContext()
      window.kortex.hideHelper()
      window.kortex.unregisterTool('get_md_file_info')
      window.kortex.unregisterTool('get_md_content')
      window.kortex.unregisterTool('set_md_content')
      window.kortex.unregisterTool('append_md_content')
      window.kortex.unregisterTool('save_md_file')
    }
  },
  methods: {
    calculateEditorHeight() {
      const headerHeight = 60;
      const padding = 20;
      this.editorHeight = window.innerHeight - headerHeight - padding;
    },

    async loadContent() {
      try {
        const response = await this.$mediaHttp.get(`/v3/dms/media/get/${this.fileKey}`, {
          responseType: 'text'
        });
        this.content = response.data;
      } catch (error) {
        console.error('Failed to load markdown file:', error);
        this.$bvToast.toast('Failed to load file', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },

    async saveMd() {
      this.saving = true;
      try {
        const base64Content = btoa(unescape(encodeURIComponent(this.content)));
        await this.$mediaHttp.put(`/v3/dms/media/${this.fileKey}`, {
          file: {
            name: this.fileName,
            content_type: 'text/markdown',
            data: base64Content
          }
        });

        this.$bvToast.toast('File saved successfully', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Failed to save markdown file:', error);
        this.$bvToast.toast('Failed to save file', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style lang="scss" scoped>
$dark-background: var(--bg-card);
$dark-hard-background: var(--form-bg);
$dark-border2: var(--border-default);

.md-file-editor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: $dark-background;
}

.editor-header {
  background: $dark-hard-background;
  border-bottom: 1px solid $dark-border2;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .back-button {
    padding: 0;
    color: var(--text-primary);
  }

  h4 {
    color: var(--text-primary);
    font-size: 14px;
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.editor-body {
  flex: 1;
  display: flex;
  overflow: hidden;

  &.edit .editor-pane {
    width: 100%;
  }

  &.preview .preview-pane {
    width: 100%;
  }

  &.split {
    .editor-pane,
    .preview-pane {
      width: 50%;
    }

    .editor-pane {
      border-right: 1px solid $dark-border2;
    }
  }
}

.editor-pane {
  overflow: hidden;
  flex-shrink: 0;
}

.preview-pane {
  overflow-y: auto;
  flex-shrink: 0;
  background: $dark-background;
}

.preview-content {
  padding: 24px 32px;
  color: var(--text-primary);
  max-width: 800px;
  margin: 0 auto;

  ::v-deep {
    h1, h2, h3, h4, h5, h6 {
      color: var(--text-primary);
      margin-top: 1.5em;
      margin-bottom: 0.5em;
      line-height: 1.3;
    }

    h1 { font-size: 2em; border-bottom: 1px solid $dark-border2; padding-bottom: 0.3em; }
    h2 { font-size: 1.5em; border-bottom: 1px solid $dark-border2; padding-bottom: 0.3em; }
    h3 { font-size: 1.25em; }

    p {
      margin-bottom: 1em;
      line-height: 1.7;
    }

    code {
      background: var(--form-bg);
      border: 1px solid $dark-border2;
      border-radius: 4px;
      padding: 2px 6px;
      font-size: 0.9em;
      font-family: 'Courier New', monospace;
    }

    pre {
      background: var(--form-bg);
      border: 1px solid $dark-border2;
      border-radius: 6px;
      padding: 16px;
      overflow-x: auto;
      margin-bottom: 1em;

      code {
        background: none;
        border: none;
        padding: 0;
        font-size: 0.875em;
        color: var(--text-primary, #e0e0e0);
      }
    }

    blockquote {
      border-left: 4px solid var(--primary-start);
      margin: 0 0 1em;
      padding: 8px 16px;
      color: var(--text-secondary, #aaa);
      background: var(--form-bg);
      border-radius: 0 4px 4px 0;
    }

    ul, ol {
      padding-left: 2em;
      margin-bottom: 1em;

      li {
        margin-bottom: 0.25em;
        line-height: 1.7;
      }
    }

    table {
      border-collapse: collapse;
      width: 100%;
      margin-bottom: 1em;

      th, td {
        border: 1px solid $dark-border2;
        padding: 8px 12px;
        text-align: left;
      }

      th {
        background: var(--form-bg);
        font-weight: 600;
      }

      tr:nth-child(even) td {
        background: rgba(255, 255, 255, 0.03);
      }
    }

    a {
      color: var(--primary-start);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    img {
      max-width: 100%;
      border-radius: 4px;
    }

    hr {
      border: none;
      border-top: 1px solid $dark-border2;
      margin: 1.5em 0;
    }
  }
}
</style>
